<template>
  <section class="form-input is-relative m-b-25" :class="{ 'highlight-error': forceShowError }">
    <ValidationProvider
      :value="computedValue"
      ref="validationProvider"
      :rules="rules"
      v-slot="validationProps"
    >
      <float-label
        :fixed="hasFixedLabel"
        :label="computedPlaceholder"
        :on="showFloatLabel"
        :input-is-disabled="readOnly"
      >
        <FieldView>
          <slot name="label" />
          <!-- eslint-disable vue/no-deprecated-v-on-native-modifier -->
          <template v-if="type === 'number' && noLeadingZeros">
            <InputView
              autocomplete="off"
              v-model="computedValue"
              :class="getClasses(validationProps)"
              :custom-class="customClass"
              :type="type"
              :icon="getIcon(icons.left)"
              :icon-right="getIcon(icons.right)"
              icon-pack="cls"
              :disabled="readOnly"
              :loading="loading"
              ref="refInput"
              :placeholder="computedPlaceholder"
              :use-html5-validation="false"
              :maxlength="maxLength"
              @focus="onFocus"
              @blur="onBlur"
              @keyup="onKeyUp"
            />
          </template>
          <template v-else>
            <InputView
              v-model="computedValue"
              autocomplete="off"
              :class="getClasses(validationProps)"
              :custom-class="customClass"
              :type="type"
              :icon="getIcon(icons.left)"
              :icon-right="getIcon(icons.right)"
              icon-pack="cls"
              :disabled="readOnly"
              :loading="loading"
              ref="refInput"
              :placeholder="computedPlaceholder"
              :use-html5-validation="false"
              :maxlength="maxLength"
              :rows="rows"
              @focus="onFocus"
              @blur="onBlur"
              @keyup="onKeyUp"
            />
          </template>
          <!-- eslint-enable vue/no-deprecated-v-on-native-modifier -->
          <slot name="button" />
          <slot name="error" v-bind="validationProps">
            <template v-if="hasValidationErrors(validationProps)">
              <span class="validation-text">
                {{ getErrorFormattedText(validationProps) }}
              </span>
            </template>
          </slot>
        </FieldView>
        <FormErrorRightIcon />
      </float-label>
      <slot
        v-if="!hasValidationErrors(validationProps) && (subLabel || $slots.subLabel)"
        name="subLabel"
        :has-errors="hasValidationErrors(validationProps)"
      >
        <FormSubLabelTooltip
          v-if="hasTooltip"
          :right-label="rightSubLabel"
          :tooltip-label="tooltipLabel"
          :left-label="subLabel"
          :is-vertical="isVertical"
        />
        <FormSubLabel v-else :label="subLabel" :hide-icon="hideIcon" />
      </slot>
    </ValidationProvider>
  </section>
</template>

<script>
import ValidationProvider from '/@/modules/core/form/components/ValidationProvider.vue';
import FloatLabel from '/@/modules/core/form/components/FloatLabel.vue';
import { FormComputedValueMixin } from '/@/modules/core/form/mixins/FormComputedValueMixin';
import { FormValidationMixin } from '/@/modules/core/form/mixins/FormValidationMixin';
import FormErrorRightIcon from '/@/modules/core/form/components/FormErrorRightIcon.vue';
import FormSubLabel from '/@/modules/core/form/components/FormSubLabel.vue';
import Lang from '/@/modules/core/app/services/Lang';
import { FormLabelMixin } from '/@/modules/core/form/mixins/FormLabelMixin';
import FormSubLabelTooltip from '/@/modules/core/form/components/FormSubLabelTooltip.vue';
import InputView from '@/modules/core/ui/components/wrappers/InputView.vue';
import FieldView from '@/modules/core/ui/components/wrappers/FieldView.vue';
import { FormEvent } from '@/modules/core/form/constants/FormEvent';
import EventBus from '@/modules/core/app/helpers/EventBus';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormInput',
  emits: ['input', 'focus', 'keyup', 'blur'],
  slotsDefinition: {
    label: {},
    button: {},
    error: { validationProps: Object },
    subLabel: { hasErrors: Boolean }
  },
  mixins: [FormComputedValueMixin, FormValidationMixin, FormLabelMixin],
  components: {
    FormSubLabel,
    FormErrorRightIcon,
    ValidationProvider,
    FloatLabel,
    FormSubLabelTooltip,
    InputView,
    FieldView
  },
  props: {
    label: { type: String },
    subLabel: { type: String, default: null },
    placeholder: { type: String },
    readOnly: { type: Boolean, default: false },
    type: { type: String, default: 'text' },
    icon: { type: String },
    iconRight: { type: String },
    maxLength: { type: [Number, String], default: null },
    rows: { type: [Number, String], default: null },
    customClass: { type: String, default: '' },
    showFloatLabel: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    noLeadingZeros: { type: Boolean, default: true },
    hasFixedLabel: { type: Boolean, default: false }
  },
  computed: {
    computedPlaceholder() {
      return this.placeholder || this.$t('core.enter-label', { label: this.column?.label || '' });
    },
    icons() {
      const isDollarCurrencyForQuebec = this.icon === 'currency-dollar' && Lang.isFrench();

      return {
        left: isDollarCurrencyForQuebec ? null : this.icon,
        right: isDollarCurrencyForQuebec ? this.icon : this.iconRight
      };
    }
  },
  methods: {
    onInput(value) {
      this.$emit('input', value);
    },
    onFocus(event) {
      this.$emit('focus', event);
      EventBus.signal(FormEvent.ON_INPUT_FOCUS, this);
    },
    onKeyUp(event) {
      this.$emit('keyup', event);
    },
    onBlur(event) {
      this.$emit('blur', event);
      EventBus.signal(FormEvent.ON_INPUT_BLUR, this);
    },
    getIcon(name) {
      return name ? `cls-${name}` : null;
    },
    focus() {
      this.$refs.refInput?.focus?.();
    }
  }
};
</script>

<style scoped>
.form-input :deep(.field:not(:last-child)) {
  margin-bottom: 0;
}

@media (max-width: CE_MOBILE) {
  .form-input :deep(.field .control .input + .icon) {
    margin-top: 5px !important;
  }
}
</style>
